'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import ServiceDetailModal from './ServiceDetailModal';
import ServiceFilterBar from './ServiceFilterBar';
import PerformanceMetrics from './PerformanceMetrics';
import ServiceComparisonMatrix from './ServiceComparisonMatrix';
import TestimonialSection from './TestimonialSection';

interface ServiceData {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  features: string[];
  technicalSpecs: string[];
  caseStudies: Array<{
    title: string;
    client: string;
    result: string;
  }>;
  capabilities: string[];
  responseTime: string;
  coverage: string;
  certifications: string[];
  caseStudyCount: number;
  category: string;
  industries: string[];
}

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All Services');
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: ServiceData[] = [
  {
    id: 1,
    title: 'Telecom Tower Installation & Maintenance',
    description:
    'Comprehensive tower infrastructure services including site survey, foundation work, tower erection, antenna installation, and ongoing maintenance. Specialized in greenfield and rooftop installations with 24/7 emergency support.',
    image: "https://images.unsplash.com/photo-1730387793515-35223fc47561",
    alt: 'Tall telecommunications tower with multiple antennas against blue sky with white clouds',
    icon: 'SignalIcon',
    features: [
    'Complete tower erection and commissioning',
    'RF optimization and antenna alignment',
    '24/7 emergency response team',
    'Preventive maintenance programs',
    'Tower structural health monitoring',
    'Lightning protection systems'],

    technicalSpecs: [
    'Tower heights: 30m to 80m',
    'Load capacity: Up to 15 tons',
    'Wind zone compliance: All zones',
    'Seismic zone compliance: Zone II to V',
    'Foundation types: Pile, raft, and isolated',
    'Antenna systems: 2G/3G/4G/5G compatible'],

    caseStudies: [
    {
      title: 'Pan-India Tower Rollout',
      client: 'Major Telecom Operator',
      result: '500+ towers installed in 18 months with 99.8% uptime'
    },
    {
      title: 'Emergency Tower Restoration',
      client: 'Government Telecom Department',
      result: 'Critical infrastructure restored within 48 hours post-disaster'
    }],

    capabilities: [
    'Multi-vendor equipment integration',
    'Remote monitoring systems',
    'Structural analysis and reinforcement',
    'Environmental compliance management'],

    responseTime: '4 hours',
    coverage: 'Pan-India',
    certifications: ['ISO 9001:2015', 'ISO 14001:2015', 'OHSAS 18001'],
    caseStudyCount: 45,
    category: 'Telecom Infrastructure',
    industries: ['Telecommunications', 'Government']
  },
  {
    id: 2,
    title: 'Electrical Substation Installation',
    description:
    'End-to-end electrical substation solutions from design to commissioning. Expertise in HT/LT substations, switchgear installation, transformer commissioning, and power distribution systems for industrial and commercial applications.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_126adf4be-1767090392872.png",
    alt: 'Industrial electrical substation with transformers and high voltage equipment in outdoor setting',
    icon: 'BoltIcon',
    features: [
    'HT/LT substation design and installation',
    'Transformer commissioning and testing',
    'Switchgear and panel installation',
    'Power quality analysis',
    'Energy audit and optimization',
    'SCADA system integration'],

    technicalSpecs: [
    'Voltage levels: 11kV to 220kV',
    'Transformer capacity: 100kVA to 10MVA',
    'Protection schemes: Numerical relays',
    'Earthing systems: TN-S, TN-C-S, TT',
    'Compliance: CEA regulations',
    'Testing: As per IEC standards'],

    caseStudies: [
    {
      title: 'Industrial Park Power Infrastructure',
      client: 'State Industrial Development Corporation',
      result: '25MVA substation commissioned with 99.9% reliability'
    },
    {
      title: 'Commercial Complex Electrical System',
      client: 'Real Estate Developer',
      result: 'Complete electrical infrastructure for 2 million sq ft complex'
    }],

    capabilities: [
    'Load flow analysis',
    'Fault level calculations',
    'Protection coordination studies',
    'Harmonic analysis and mitigation'],

    responseTime: '6 hours',
    coverage: 'National',
    certifications: ['CEA Approved', 'ISO 9001:2015', 'Electrical License Class A'],
    caseStudyCount: 38,
    category: 'Electrical Systems',
    industries: ['Industrial', 'Commercial', 'Government']
  },
  {
    id: 3,
    title: 'Annual Maintenance Contracts (AMC)',
    description:
    'Comprehensive maintenance programs for telecom and electrical infrastructure. Preventive maintenance, emergency support, spare parts management, and performance monitoring to ensure maximum uptime and operational efficiency.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_183236a07-1766497234725.png",
    alt: 'Technician in safety gear performing maintenance work on industrial equipment with tools',
    icon: 'WrenchScrewdriverIcon',
    features: [
    'Scheduled preventive maintenance',
    '24/7 emergency breakdown support',
    'Spare parts inventory management',
    'Performance monitoring and reporting',
    'Predictive maintenance using IoT',
    'Compliance and safety audits'],

    technicalSpecs: [
    'Response time: 2-4 hours',
    'Uptime guarantee: 99.5%+',
    'Maintenance frequency: Monthly/Quarterly',
    'Remote monitoring: 24/7',
    'Spare parts availability: 48 hours',
    'Documentation: Digital maintenance logs'],

    caseStudies: [
    {
      title: 'Telecom Network AMC',
      client: 'Regional Telecom Circle',
      result: 'Maintained 1200+ sites with 99.7% uptime over 3 years'
    },
    {
      title: 'Industrial Facility Maintenance',
      client: 'Manufacturing Company',
      result: 'Reduced downtime by 65% through predictive maintenance'
    }],

    capabilities: [
    'Multi-site coordination',
    'Vendor-neutral support',
    'Condition-based monitoring',
    'Asset lifecycle management'],

    responseTime: '2 hours',
    coverage: 'Pan-India',
    certifications: ['ISO 9001:2015', 'ISO 55001 (Asset Management)'],
    caseStudyCount: 52,
    category: 'Maintenance & Support',
    industries: ['Telecommunications', 'Industrial', 'Commercial']
  },
  {
    id: 4,
    title: 'Project Management & Turnkey Solutions',
    description:
    'Complete project lifecycle management from feasibility study to handover. Expertise in managing complex infrastructure projects with multi-vendor coordination, quality assurance, and on-time delivery.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19293a26f-1767898858368.png",
    alt: 'Professional team reviewing construction blueprints and plans on table in modern office',
    icon: 'ClipboardDocumentCheckIcon',
    features: [
    'End-to-end project management',
    'Multi-vendor coordination',
    'Quality assurance and testing',
    'Regulatory compliance management',
    'Risk assessment and mitigation',
    'Documentation and handover'],

    technicalSpecs: [
    'Project size: ₹1 Cr to ₹100 Cr+',
    'Timeline: 3 months to 24 months',
    'Team size: 10 to 200+ personnel',
    'Quality standards: ISO 9001',
    'Safety compliance: OHSAS 18001',
    'Reporting: Weekly/Monthly dashboards'],

    caseStudies: [
    {
      title: 'Smart City Infrastructure Project',
      client: 'Municipal Corporation',
      result: 'Delivered ₹50 Cr project 2 months ahead of schedule'
    },
    {
      title: 'Telecom Network Expansion',
      client: 'National Telecom Operator',
      result: 'Managed rollout of 800+ sites across 5 states'
    }],

    capabilities: [
    'Agile project methodologies',
    'Resource optimization',
    'Stakeholder management',
    'Change management processes'],

    responseTime: '24 hours',
    coverage: 'National',
    certifications: ['PMP Certified Team', 'ISO 9001:2015', 'PRINCE2'],
    caseStudyCount: 28,
    category: 'Project Management',
    industries: ['Government', 'Telecommunications', 'Industrial']
  },
  {
    id: 5,
    title: 'Fiber Optic Network Installation',
    description:
    'Specialized fiber optic cable laying, splicing, and testing services for telecom and enterprise networks. Expertise in underground, aerial, and building cabling with fusion splicing and OTDR testing.',
    image: "https://images.unsplash.com/photo-1543946602-8496af5aaa53",
    alt: 'Close-up of illuminated blue fiber optic cables with light transmission in dark background',
    icon: 'CircleStackIcon',
    features: [
    'Underground and aerial cable laying',
    'Fusion splicing and termination',
    'OTDR testing and certification',
    'Fiber network design',
    'Duct and manhole construction',
    'Network documentation'],

    technicalSpecs: [
    'Cable types: Single-mode, Multi-mode',
    'Core count: 12 to 288 fibers',
    'Splicing loss: &lt;0.05 dB',
    'Testing: OTDR, Power meter, Light source',
    'Standards: ITU-T G.652D, G.657A',
    'Documentation: As-built drawings'],

    caseStudies: [
    {
      title: 'Metro Fiber Network',
      client: 'City Municipal Corporation',
      result: '150 km fiber network deployed for smart city initiative'
    },
    {
      title: 'Enterprise Campus Network',
      client: 'IT Park Developer',
      result: 'High-density fiber infrastructure for 50+ buildings'
    }],

    capabilities: [
    'Route survey and planning',
    'Right-of-way management',
    'Underground utility detection',
    'Network redundancy design'],

    responseTime: '8 hours',
    coverage: 'Regional',
    certifications: ['FOA Certified', 'ISO 9001:2015'],
    caseStudyCount: 34,
    category: 'Telecom Infrastructure',
    industries: ['Telecommunications', 'Government', 'Commercial']
  },
  {
    id: 6,
    title: 'DG Set Installation & Maintenance',
    description:
    'Complete diesel generator solutions including site preparation, installation, commissioning, and ongoing maintenance. Expertise in standby and prime power applications with remote monitoring capabilities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c11462c-1766488795028.png",
    alt: 'Large industrial diesel generator set in outdoor installation with control panel and exhaust system',
    icon: 'CpuChipIcon',
    features: [
    'DG set sizing and selection',
    'Site preparation and installation',
    'Load bank testing',
    'Synchronization and paralleling',
    'Remote monitoring systems',
    'Fuel management solutions'],

    technicalSpecs: [
    'Capacity range: 25kVA to 2000kVA',
    'Voltage: 415V, 3-phase',
    'Fuel type: Diesel, Bio-diesel',
    'Noise levels: As per CPCB norms',
    'Emission standards: CPCB compliant',
    'Automation: AMF, Auto synchronization'],

    caseStudies: [
    {
      title: 'Data Center Power Backup',
      client: 'IT Services Company',
      result: '2x1000kVA DG sets with N+1 redundancy, 99.99% availability'
    },
    {
      title: 'Hospital Emergency Power',
      client: 'Multi-specialty Hospital',
      result: 'Seamless power backup with &lt;10 second switchover time'
    }],

    capabilities: [
    'Load analysis and sizing',
    'Acoustic enclosure design',
    'Exhaust system engineering',
    'Fuel tank design and installation'],

    responseTime: '4 hours',
    coverage: 'Pan-India',
    certifications: ['CPCB Approved', 'ISO 9001:2015'],
    caseStudyCount: 41,
    category: 'Electrical Systems',
    industries: ['Commercial', 'Industrial', 'Telecommunications']
  }];


  const performanceMetrics = [
  {
    label: 'Average Response Time',
    value: '3.5 hrs',
    icon: 'ClockIcon',
    trend: '↓ 15%'
  },
  {
    label: 'Project Completion Rate',
    value: '98.5%',
    icon: 'CheckCircleIcon',
    trend: '↑ 2%'
  },
  {
    label: 'Client Retention',
    value: '94%',
    icon: 'UserGroupIcon',
    trend: '↑ 5%'
  },
  {
    label: 'Safety Record',
    value: '0 LTI',
    icon: 'ShieldCheckIcon',
    trend: '365 days'
  }];


  const comparisonFeatures = [
  { feature: '24/7 Emergency Support', basic: false, standard: true, premium: true },
  { feature: 'Preventive Maintenance', basic: true, standard: true, premium: true },
  { feature: 'Remote Monitoring', basic: false, standard: true, premium: true },
  { feature: 'Spare Parts Included', basic: false, standard: false, premium: true },
  { feature: 'Dedicated Account Manager', basic: false, standard: false, premium: true },
  { feature: 'Performance Reports', basic: false, standard: true, premium: true },
  { feature: 'Priority Response', basic: false, standard: false, premium: true },
  { feature: 'Annual Health Audit', basic: false, standard: true, premium: true },
  { feature: 'Predictive Maintenance', basic: false, standard: false, premium: true },
  { feature: 'Training Programs', basic: false, standard: false, premium: true }];


  const testimonials = [
  {
    name: 'Rajesh Kumar',
    position: 'Chief Engineer',
    company: 'State Telecom Department',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_198b27636-1763293917601.png",
    alt: 'Professional Indian man in formal blue shirt smiling at camera in office setting',
    quote:
    'Ativanam Solutions has been our trusted partner for tower maintenance across the state. Their response time and technical expertise are unmatched in the industry.',
    service: 'Telecom Tower Maintenance',
    rating: 5
  },
  {
    name: 'Priya Sharma',
    position: 'Facility Manager',
    company: 'Tech Park Developers',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_124bbe54a-1763295891998.png",
    alt: 'Professional Indian woman in business attire with confident smile in modern office',
    quote:
    'The electrical substation project was completed ahead of schedule with exceptional quality. Their project management skills and attention to safety standards impressed us.',
    service: 'Electrical Substation Installation',
    rating: 5
  },
  {
    name: 'Amit Patel',
    position: 'Operations Head',
    company: 'Manufacturing Industries Ltd',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e337c846-1763294002427.png",
    alt: 'Middle-aged Indian businessman in grey suit with professional demeanor in corporate setting',
    quote:
    'Their AMC service has significantly reduced our downtime. The predictive maintenance approach has saved us substantial costs over the past two years.',
    service: 'Annual Maintenance Contract',
    rating: 5
  },
  {
    name: 'Sunita Reddy',
    position: 'Project Director',
    company: 'Smart City Mission',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c613bef9-1763301359409.png",
    alt: 'Senior Indian woman professional in formal attire with warm smile in government office',
    quote:
    'Ativanam delivered our fiber optic network project with remarkable precision. Their documentation and compliance management made our audit process seamless.',
    service: 'Fiber Optic Network Installation',
    rating: 5
  }];


  const filteredServices = services.filter((service) => {
    const matchesCategory =
    selectedCategory === 'All Services' || service.category === selectedCategory;
    const matchesIndustry =
    selectedIndustry === 'All Industries' ||
    service.industries.includes(selectedIndustry);
    const matchesSearch =
    searchQuery === '' ||
    service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesIndustry && matchesSearch;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="h-24 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-headline font-bold text-primary mb-4">
            Our Service Portfolio
          </h1>
          <p className="text-lg font-body text-textSecondary max-w-3xl mx-auto">
            Comprehensive infrastructure solutions backed by technical expertise, proven
            track record, and unwavering commitment to quality and safety standards.
          </p>
        </div>

        <PerformanceMetrics metrics={performanceMetrics} />

        <ServiceFilterBar
          selectedCategory={selectedCategory}
          selectedIndustry={selectedIndustry}
          searchQuery={searchQuery}
          onCategoryChange={setSelectedCategory}
          onIndustryChange={setSelectedIndustry}
          onSearchChange={setSearchQuery} />


        {filteredServices.length === 0 ?
        <div className="text-center py-12">
            <p className="text-lg font-body text-textSecondary">
              No services found matching your criteria. Please adjust your filters.
            </p>
          </div> :

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredServices.map((service) =>
          <ServiceCard
            key={service.id}
            title={service.title}
            description={service.description}
            image={service.image}
            alt={service.alt}
            icon={service.icon}
            features={service.features}
            caseStudyCount={service.caseStudyCount}
            responseTime={service.responseTime}
            onLearnMore={() => setSelectedService(service)} />

          )}
          </div>
        }

        <ServiceComparisonMatrix features={comparisonFeatures} />

        <TestimonialSection testimonials={testimonials} />

        <div className="bg-primary rounded-lg p-8 text-center">
          <h2 className="text-2xl font-headline font-bold text-primary-foreground mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-base font-body text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
            Our team of experts can design and deliver tailored infrastructure solutions
            to meet your specific requirements. Contact us for a detailed consultation.
          </p>
          <button className="px-8 py-3 bg-cta text-cta-foreground rounded-md text-base font-cta font-semibold shadow-cta transition-all duration-300 hover:-translate-y-0.5 hover:shadow-modal">
            Request Custom Quote
          </button>
        </div>
      </div>

      <ServiceDetailModal
        service={selectedService}
        onClose={() => setSelectedService(null)} />

    </div>);

}